import { DatePipe } from '@angular/common';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormField } from '@angular/material/form-field';
import { MAT_FORM_FIELD_CONTROL_STUB } from './app.consts';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, RouteReuseStrategy, withRouterConfig } from '@angular/router';
import { provideTranslateService, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { withNgxsFormPlugin } from '@ngxs/form-plugin';
import { withNgxsLoggerPlugin } from '@ngxs/logger-plugin';
import { withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { provideStore } from '@ngxs/store';
import {
  authConfigLoader,
  AuthenticationService,
  AuthenticationState,
  ConfigService,
  jwtInterceptor,
  LanguageState,
  monitorInterceptor,
  networkErrorInterceptor,
  plantInterceptor,
  serverErrorInterceptor,
  SignalRService,
  tenantInterceptor,
} from '@orion-platform/core';
import { StsConfigLoader, provideAuth } from 'angular-auth-oidc-client';
import moment from 'moment';
import { provideToastr } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { CustomRouteReuseStrategy } from './utils/custom-route-reuse-strategy';
import { getActiveLanguage } from './utils/general';

/**
 * Patches Angular Material's `MatFormField._control` getter so it never
 * returns `undefined` on the first CD tick before the
 * `ContentChild(MatFormFieldControl)` query resolves. Without this,
 * mat-form-field's own template bindings (`_control.disabled`,
 * `_control.errorState`, etc.) throw `TypeError` and Material aborts
 * its own render pass, leaving the form field visually + functionally
 * broken. The stub returned in the transient window is defined in
 * `app.consts.ts`; the shape it satisfies is defined in `app.types.ts`.
 *
 * Runs once at module load, before any component instantiates a
 * mat-form-field. Idempotent via the `__orionControlGetterPatched`
 * marker so hot-reload / test re-imports don't stack multiple wrappers.
 */
function patchMatFormFieldControlGetter(): void {
  const proto = MatFormField.prototype as unknown as {
    __orionControlGetterPatched?: boolean;
  };
  if (proto.__orionControlGetterPatched) return;

  const descriptor = Object.getOwnPropertyDescriptor(MatFormField.prototype, '_control');
  if (!descriptor?.get) return;

  const originalGet = descriptor.get;
  Object.defineProperty(MatFormField.prototype, '_control', {
    configurable: true,
    get(): unknown {
      const value = originalGet.call(this);
      return value ?? MAT_FORM_FIELD_CONTROL_STUB;
    },
    set: descriptor.set,
  });
  proto.__orionControlGetterPatched = true;
}

patchMatFormFieldControlGetter();

/**
 * Pins moment's `en` locale to ISO week rules — Monday first, week 1 is the
 * one containing Jan 4 — for the whole app.
 *
 * Material's `MomentDateAdapter` derives `getFirstDayOfWeek()` from the active
 * moment locale, and components provide `MAT_DATE_LOCALE` as either `'cs_CZ'`
 * (resolves to `cs`, already Monday-first) or `'en_EN'` — which is not a real
 * locale, so moment falls back to `en` and starts weeks on Sunday. That split
 * left a third of the calendars Sunday-first while the rest compensated by
 * mutating `getFirstDayOfWeek` on their own adapter instance, one component at
 * a time. A Sunday-first grid also mislabels the ISO week-number column: the
 * row's first cell is a Sunday, which ISO counts as the previous week, so the
 * same number renders twice.
 *
 * Setting it here once makes every calendar Monday-first with no per-component
 * setup. Safe because `isoWeek()` is Monday-based by definition and therefore
 * untouched, and nothing in the app reads moment's locale-week API
 * (`week()` / `startOf('week')`).
 */
function pinMomentWeekToIso(): void {
  moment.updateLocale('en', { week: { dow: 1, doy: 4 } });
}

pinMomentWeekToIso();

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideAnimations(),
    provideRouter(routes, withRouterConfig({ onSameUrlNavigation: 'reload' })),
    provideToastr({
      positionClass: 'toast-bottom-right',
      maxOpened: 5,
      autoDismiss: false,
      newestOnTop: true,
      preventDuplicates: false,
      timeOut: 20000,
      extendedTimeOut: 0,
      closeButton: false,
      progressBar: false,
      easeTime: 300,
    }),
    { provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'fill', floatLabel: 'auto' },
    },
    DatePipe,
    AuthenticationService,
    SignalRService,
    provideStore(
      [AuthenticationState, LanguageState],
      {
        developmentMode: !environment.production,
        compatibility: {
          strictContentSecurityPolicy: true,
        },
      },
      withNgxsStoragePlugin({
        keys: [
          'authentication.token',
          'authentication.tenants',
          'authentication.activeTenant',
          'authentication.isLoggedIn',
          'authentication.username',
          'authentication.plant',
          'authentication.plants',
          'feedback.feedbackData',
          'supplyvisibility.partNumberListParameters',
          'supplyvisibility.DefaultDashboardData',
        ],
      }),
      withNgxsReduxDevtoolsPlugin({ disabled: environment.production }),
      withNgxsLoggerPlugin({ disabled: environment.production }),
      withNgxsFormPlugin(),
    ),
    provideHttpClient(
      withXhr(),
      withInterceptors([
        jwtInterceptor,
        plantInterceptor,
        tenantInterceptor,
        networkErrorInterceptor,
        serverErrorInterceptor,
        monitorInterceptor,
      ]),
    ),
    provideAuth({
      // A loader rather than the constant: the settings that differ between
      // environments come from the file the deployment mounts, and only the
      // application's own flow settings stay compiled in.
      loader: {
        provide: StsConfigLoader,
        useFactory: authConfigLoader,
      },
    }),
    provideAppInitializer(async () => {
      const configService = inject(ConfigService);
      await configService.loadConfig();
    }),
    provideAppInitializer(async () => {
      const translate = inject(TranslateService);
      const language = getActiveLanguage();
      translate.setFallbackLang('en');
      await firstValueFrom(translate.use(language.code));
    }),
    provideTranslateService({}),
    provideTranslateHttpLoader({
      prefix: '/i18n/app/',
      suffix: '.json?' + 'v=0001',
    }),
  ],
};
